import React from 'react';
import { Calendar } from 'lucide-react';
import clsx from 'clsx';
import styles from './DateInput.module.css';

export interface DateInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  ref?: React.Ref<HTMLInputElement>;
  label?: string | React.ReactNode;
  error?: string | React.ReactNode;
  type?: 'date' | 'datetime-local' | 'time';
  showIcon?: boolean;
  wrapperClassName?: string;
  dataTestId?: string;
}

function DateInput({
  ref,
  label,
  error,
  type = 'date',
  showIcon = false,
  required,
  className,
  wrapperClassName,
  id,
  dataTestId,
  ...props
}: DateInputProps) {
  // Generate ID for label association if not provided
  const generatedId = React.useId();
  const inputId = id || `date-input-${generatedId}`;

  return (
    <div className={clsx(styles.dateInputWrapper, wrapperClassName)}>
      {label && (
        <label htmlFor={inputId} className={styles.label} data-test-id={dataTestId ? `${dataTestId}-label` : undefined}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}

      <div className={styles.inputContainer}>
        {showIcon && (
          <span className={styles.calendarIcon}>
            <Calendar size={16} />
          </span>
        )}

        <input
          id={inputId}
          type={type}
          className={clsx(
            styles.dateInput,
            showIcon && styles.dateInputWithIcon,
            error && styles.dateInputError,
            className
          )}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${inputId}-error` : undefined}
          data-test-id={dataTestId ? `${dataTestId}-input` : undefined}
          {...props}
          ref={ref}
        />
      </div>

      {error && (
        <div
          id={`${inputId}-error`}
          className={styles.errorMessage}
          role="alert"
          data-test-id={dataTestId ? `${dataTestId}-error` : 'validation-error'}
        >
          {error}
        </div>
      )}
    </div>
  );
}

export default DateInput;
